import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Building2, ArrowRight, MapPin, Coffee, Printer, Monitor, Snowflake, Wifi, Calendar, CheckCircle, Loader2, Phone, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { format, isBefore, startOfDay } from 'date-fns';
import { fr } from 'date-fns/locale';
import axios from 'axios';
import { useCmsContent } from '../hooks/useCmsContent';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ROOM_IMAGE = "https://customer-assets.emergentagent.com/job_bf1ebca5-1fd5-4042-b351-0048fc2281bc/artifacts/2n81eydn_20230524_153410.jpg";

const features = [
  { icon: MapPin, label: 'Centre-ville' },
  { icon: Coffee, label: 'Café & collations' },
  { icon: Printer, label: 'Impressions offertes' },
  { icon: Monitor, label: 'Vidéoprojecteur' },
  { icon: Snowflake, label: 'Climatisation' },
  { icon: Wifi, label: 'Wi-Fi' }
];

const pricingData = [
  { name: 'Entreprises', discount: 'Tarif standard', hourly: '2 200 F/h', halfDay: '8 500 F/½ jour', fullDay: '13 500 F/jour' },
  { name: 'Associations', discount: '-25%', hourly: '1 650 F/h', halfDay: '6 375 F/½ jour', fullDay: '10 125 F/jour' },
  { name: 'Étudiants & UPF', discount: '-40%', hourly: '1 320 F/h', halfDay: '5 100 F/½ jour', fullDay: '8 100 F/jour' }
];

const pricingValues = {
  entreprises: { hourly: '2 200', halfDay: '8 500', fullDay: '13 500' },
  associations: { hourly: '1 650', halfDay: '6 375', fullDay: '10 125' },
  etudiants: { hourly: '1 320', halfDay: '5 100', fullDay: '8 100' }
};

const FALLBACK_LOCATION = {
  hero: { title: 'Location de salles à Papeete', description: 'Des espaces professionnels climatisés et équipés pour vos réunions, formations et événements. Au cœur de Papeete, Tahiti.' },
  contact: { phone: '+689 87 73 78 15', email: 'contact@groupeaerhpolynesie.com', address: '10 Rue Georges LAGARDE, 98714 Papeete' },
};

const categoryToPackageKey = { entreprises: 'entreprise', associations: 'association', etudiants: 'etudiant' };
const formulaToPackageKey = { hourly: 'hourly', halfDay: 'halfday', fullDay: 'fullday' };

export const RoomRentalPage = () => {
  const cms = useCmsContent('location', FALLBACK_LOCATION);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [bookingOpen, setBookingOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [category, setCategory] = useState('');
  const [formula, setFormula] = useState('');
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', company: '', notes: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  useEffect(() => {
    const payment = searchParams.get('payment');
    const sessionId = searchParams.get('session_id');
    if (payment === 'success' && sessionId) {
      setPaymentSuccess(true);
      axios.get(`${API}/stripe/status/${sessionId}`).then(res => {
        if (res.data?.payment_status === 'paid') {
          toast.success('Paiement confirmé ! Votre réservation est validée.');
        }
      }).catch(() => {});
      setSearchParams({}, { replace: true });
    } else if (payment === 'cancelled') {
      toast.error('Paiement annulé. Votre réservation est en attente.');
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const calculatePrice = () => {
    if (!category || !formula) return null;
    const cat = pricingValues[category];
    if (!cat) return null;
    return cat[formula] || null;
  };

  const getPackageId = () => {
    if (!category || !formula) return null;
    const catKey = categoryToPackageKey[category];
    const fKey = formulaToPackageKey[formula];
    if (!catKey || !fKey) return null;
    return `room-${catKey}-${fKey}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedDate || !category || !formula || !formData.name || !formData.email || !formData.phone) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    const packageId = getPackageId();
    if (!packageId) {
      toast.error('Combinaison de tarif invalide');
      return;
    }
    setSubmitting(true);
    try {
      const bookingRes = await axios.post(`${API}/bookings`, {
        ...formData, date: format(selectedDate, 'yyyy-MM-dd'), category, formula,
        price: calculatePrice(), status: 'pending_payment'
      });
      const bookingId = bookingRes.data?.id;

      const stripeRes = await axios.post(`${API}/stripe/checkout`, {
        package_id: packageId,
        origin_url: window.location.origin,
        customer_name: formData.name,
        customer_email: formData.email,
        customer_phone: formData.phone,
        success_path: '/location-salles',
        cancel_path: '/location-salles',
        booking_id: bookingId,
        extra_metadata: { room: 'Salle AERH', date: format(selectedDate, 'yyyy-MM-dd'), category, formula }
      });
      if (stripeRes.data?.url) {
        window.location.href = stripeRes.data.url;
      } else {
        toast.error('Erreur lors de la création du paiement.');
        setSubmitting(false);
      }
    } catch (err) {
      toast.error("Erreur lors de l'envoi. Veuillez réessayer.");
      setSubmitting(false);
    }
  };

  const scrollToTarifs = () => {
    document.getElementById('tarifs-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="bg-[#F8FAFC]" data-testid="room-rental-page">
      {/* Payment Success Banner */}
      {paymentSuccess && (
        <div className="bg-emerald-50 border-b border-emerald-200 px-4 py-4" data-testid="payment-success-banner">
          <div className="max-w-7xl mx-auto flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0" />
            <p className="text-emerald-800 font-medium">Paiement confirmé ! Votre réservation est validée. Vous recevrez un email de confirmation.</p>
          </div>
        </div>
      )}
      {/* Main Content */}
      <div className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Hero: 2-column layout */}
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-16" data-testid="room-hero">
            {/* Left: Text */}
            <div>
              <div className="inline-flex items-center gap-2 bg-[#F59E0B] text-white text-xs font-semibold px-3 py-1 rounded-full mb-4">
                <Building2 className="w-3 h-3" />
                LOCATION DE SALLES
              </div>
              <h1 className="text-3xl sm:text-4xl font-bold text-[#1F2937] mb-4" data-testid="room-title">
                Réservez nos salles de réunion et formation
              </h1>
              <p className="text-gray-600 text-lg mb-6">
                Deux salles climatisées et équipées pour vos réunions, séminaires et formations. Capacité jusqu'à 12 personnes.
              </p>

              {/* Feature pills */}
              <div className="flex flex-wrap gap-3 mb-8" data-testid="features-list">
                {features.map((f) => (
                  <span
                    key={f.label}
                    className="flex items-center gap-1.5 text-sm text-gray-600 bg-white px-3 py-1.5 rounded-full border border-gray-200"
                  >
                    <f.icon className="w-3.5 h-3.5 text-teal-600" />
                    {f.label}
                  </span>
                ))}
              </div>

              {/* CTA Buttons */}
              <div className="flex gap-3">
                <Button
                  onClick={() => navigate('/location-salles/sites')}
                  className="bg-[#0D9488] hover:bg-[#0F766E] text-white"
                  data-testid="cta-voir-salles"
                >
                  Voir les salles
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  className="border-[#0D9488] text-[#0D9488] hover:bg-[#0D9488]/10"
                  onClick={scrollToTarifs}
                  data-testid="cta-comment-marche"
                >
                  Comment ça marche ?
                </Button>
              </div>
            </div>

            {/* Right: Image with overlay */}
            <div className="relative" data-testid="room-image-section">
              <div className="rounded-xl overflow-hidden shadow-2xl border border-gray-200">
                <img
                  alt="Salle AERH"
                  className="w-full h-80 object-cover"
                  src={ROOM_IMAGE}
                />
                <div className="absolute bottom-4 left-4 right-4 bg-white/90 backdrop-blur-sm rounded-lg p-4 border border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-sm text-[#1F2937]">Salle Tiare & Hibiscus</p>
                      <p className="text-xs text-gray-500">12 pers. max · Climatisée</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-[#0D9488]" data-testid="room-price">2 200 F/h</p>
                      <span className="bg-[#F59E0B] text-white text-xs px-2 py-0.5 rounded-full font-semibold">
                        Disponible
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Pricing Section */}
          <div id="tarifs-section" className="bg-white rounded-xl border border-gray-200 p-8 mb-12 shadow-sm" data-testid="pricing-section">
            <h3 className="text-xl font-bold text-[#1F2937] mb-6 text-center">
              Nos tarifs (XPF)
            </h3>
            <div className="grid sm:grid-cols-3 gap-6">
              {pricingData.map((cat) => (
                <div key={cat.name} className="bg-[#F8FAFC] rounded-lg p-5 text-center border border-gray-100" data-testid={`pricing-${cat.name.toLowerCase().replace(/[^a-z]/g, '')}`}>
                  <h4 className="font-semibold text-[#1F2937]">{cat.name}</h4>
                  <p className="text-xs text-[#0D9488] font-medium mb-3">{cat.discount}</p>
                  <div className="space-y-1">
                    <p className="text-sm text-gray-600">{cat.hourly}</p>
                    <p className="text-sm text-gray-600">{cat.halfDay}</p>
                    <p className="text-sm text-gray-600">{cat.fullDay}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="text-center mt-6">
              <Button
                onClick={() => setBookingOpen(true)}
                className="bg-[#0D9488] hover:bg-[#0F766E] text-white"
                data-testid="cta-reserver"
              >
                Réserver maintenant
              </Button>
            </div>
          </div>

          {/* Contact Info */}
          <div className="bg-gradient-to-br from-[#0F766E] to-[#0D9488] rounded-xl p-8 mb-12 shadow-sm" data-testid="room-contact-section">
            <div className="grid sm:grid-cols-3 gap-8 items-center">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-[#F59E0B]/15 rounded-lg flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-[#F59E0B]" />
                </div>
                <div>
                  <p className="text-[10px] text-white/40 uppercase tracking-wider">Adresse</p>
                  <p className="text-sm text-white font-medium">10 Rue Georges Lagarde, 98714 Papeete</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-[#F59E0B]/15 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Phone className="w-5 h-5 text-[#F59E0B]" />
                </div>
                <div>
                  <p className="text-[10px] text-white/40 uppercase tracking-wider">Téléphone</p>
                  <a href="tel:+68987737815" className="text-sm text-white font-medium hover:text-[#F59E0B] transition-colors">+689 87 73 78 15</a>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-[#F59E0B]/15 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5 text-[#F59E0B]" />
                </div>
                <div>
                  <p className="text-[10px] text-white/40 uppercase tracking-wider">Email</p>
                  <a href="mailto:contact@groupeaerhpolynesie.com" className="text-sm text-white font-medium hover:text-[#F59E0B] transition-colors">contact@groupeaerhpolynesie.com</a>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Booking Dialog */}
      <Dialog open={bookingOpen} onOpenChange={setBookingOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-slate-900">
              Réserver une salle
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-6 mt-4">
            <div>
              <Label className="text-base font-medium">Type de structure *</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="mt-2" data-testid="category-select">
                  <SelectValue placeholder="Sélectionnez votre catégorie" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="entreprises">Entreprise</SelectItem>
                  <SelectItem value="associations">Association (-25%)</SelectItem>
                  <SelectItem value="etudiants">Étudiant / UPF (-40%)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-base font-medium">Formule *</Label>
              <Select value={formula} onValueChange={setFormula}>
                <SelectTrigger className="mt-2" data-testid="formula-select">
                  <SelectValue placeholder="Sélectionnez une formule" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="hourly">À l'heure</SelectItem>
                  <SelectItem value="halfDay">Demi-journée</SelectItem>
                  <SelectItem value="fullDay">Journée complète</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {calculatePrice() && (
              <div className="bg-teal-50 p-4 rounded-xl">
                <p className="text-sm text-teal-700 font-medium">Tarif estimé</p>
                <p className="text-2xl font-bold text-teal-800">{calculatePrice()} F CFP</p>
              </div>
            )}
            <div>
              <Label className="text-base font-medium">Date souhaitée *</Label>
              <div className="mt-2 flex justify-center">
                <CalendarComponent
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  locale={fr}
                  disabled={(date) => isBefore(date, startOfDay(new Date())) || date.getDay() === 0}
                  className="rounded-xl border"
                  data-testid="booking-calendar"
                />
              </div>
              {selectedDate && (
                <p className="text-center mt-2 text-sm text-teal-700">
                  Date sélectionnée : {format(selectedDate, 'EEEE d MMMM yyyy', { locale: fr })}
                </p>
              )}
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">Nom complet *</Label>
                <Input id="name" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="Votre nom" className="mt-1" required data-testid="booking-name" />
              </div>
              <div>
                <Label htmlFor="company">Entreprise / Structure</Label>
                <Input id="company" value={formData.company} onChange={(e) => setFormData({...formData, company: e.target.value})} placeholder="Nom de votre structure" className="mt-1" data-testid="booking-company" />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="email">Email *</Label>
                <Input id="email" type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} placeholder="votre@email.com" className="mt-1" required data-testid="booking-email" />
              </div>
              <div>
                <Label htmlFor="phone">Téléphone *</Label>
                <Input id="phone" type="tel" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} placeholder="+689 87 XX XX XX" className="mt-1" required data-testid="booking-phone" />
              </div>
            </div>
            <div>
              <Label htmlFor="notes">Notes / Besoins particuliers</Label>
              <Input id="notes" value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})} placeholder="Précisez vos besoins..." className="mt-1" data-testid="booking-notes" />
            </div>
            <Button type="submit" className="w-full bg-[#0D9488] hover:bg-[#0F766E] text-white rounded-full py-6" disabled={submitting} data-testid="booking-submit">
              {submitting ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Redirection vers le paiement...</> : 'Réserver et payer en ligne'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RoomRentalPage;
